import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin panel to reconcile stuck SerdiPay transactions (webhook not received).
 * - Lists pending SerdiPay transactions from the last 48h
 * - Allows admin to mark them as completed/failed using either:
 *   - the internal transaction_id, OR
 *   - the SerdiPay transaction_id (SD...) copied from SerdiPay dashboard
 */
function AdminPaymentsReconcile({ refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState('');
  const [manualSdpId, setManualSdpId] = useState('');
  const [manualStatus, setManualStatus] = useState('completed');
  const [manualNote, setManualNote] = useState('');
  const [webhooks, setWebhooks] = useState([]);
  const [showWebhooks, setShowWebhooks] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchPending = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/payments/serdipay/pending?hours=72`, {
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setError(`Erreur chargement: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/serdipay/webhooks/recent?limit=20`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setWebhooks(data.items || []);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchPending();
    fetchWebhooks();
  }, [refreshKey]);

  const reconcile = async ({ transaction_id, serdipay_transaction_id, status, note }) => {
    setBusyId(transaction_id || serdipay_transaction_id || 'manual');
    setError('');
    setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/payments/serdipay/reconcile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ transaction_id, serdipay_transaction_id, status, note }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Échec');
      setMsg(
        `Transaction ${data.transaction_id} marquée comme ${data.new_status}` +
          (data.appointment_id ? ` — RDV ${data.appointment_id} confirmé.` : '.')
      );
      await fetchPending();
    } catch (e) {
      setError(`Erreur: ${e.message}`);
    } finally {
      setBusyId('');
    }
  };

  const handleManualReconcile = (e) => {
    e.preventDefault();
    if (!manualSdpId.trim()) {
      setError("Saisissez l'ID SerdiPay (SD...)");
      return;
    }
    reconcile({
      serdipay_transaction_id: manualSdpId.trim(),
      status: manualStatus,
      note: manualNote,
    });
    setManualSdpId('');
    setManualNote('');
  };

  return (
    <div data-testid="admin-payments-reconcile" style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#0F172A' }}>Réconciliation SerdiPay</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            Utilisez cette page quand le webhook SerdiPay n'arrive pas (paiement débité, RDV bloqué en attente).
          </p>
        </div>
        <button
          onClick={() => { fetchPending(); fetchWebhooks(); }}
          disabled={loading}
          className="btn-secondary"
          data-testid="reconcile-refresh"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Rafraîchir
        </button>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', border: '1px solid #F87171', color: '#991B1B', padding: 10, borderRadius: 8, marginBottom: 12 }} data-testid="reconcile-error">
          <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {error}
        </div>
      )}
      {msg && (
        <div style={{ background: '#DCFCE7', border: '1px solid #4ADE80', color: '#166534', padding: 10, borderRadius: 8, marginBottom: 12 }} data-testid="reconcile-success">
          <CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {msg}
        </div>
      )}

      {/* Manual reconcile block (by SerdiPay ID from their dashboard) */}
      <div style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: 12, padding: 16, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 8, color: '#0F172A' }}>
          Confirmer un paiement à partir du dashboard SerdiPay
        </div>
        <p style={{ margin: '0 0 12px', color: '#475569', fontSize: '0.88rem' }}>
          Copiez l'ID SerdiPay (ex: <code>SD260913SRIHY</code>) depuis app.serdipay.com et saisissez-le ci-dessous.
        </p>
        <form onSubmit={handleManualReconcile} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="SD260913SRIHY"
            value={manualSdpId}
            onChange={(e) => setManualSdpId(e.target.value.toUpperCase())}
            data-testid="reconcile-sdp-id-input"
            style={{ padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8, fontFamily: 'monospace' }}
            required
          />
          <select
            value={manualStatus}
            onChange={(e) => setManualStatus(e.target.value)}
            data-testid="reconcile-status-select"
            style={{ padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8 }}
          >
            <option value="completed">Marquer réussi</option>
            <option value="failed">Marquer échoué</option>
          </select>
          <button
            type="submit"
            className="btn-primary"
            disabled={busyId === 'manual'}
            data-testid="reconcile-submit-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Search size={16} /> Valider
          </button>
          <input
            type="text"
            placeholder="Note (optionnel)"
            value={manualNote}
            onChange={(e) => setManualNote(e.target.value)}
            data-testid="reconcile-note-input"
            style={{ gridColumn: '1 / -1', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8 }}
          />
        </form>
      </div>

      {/* Pending list */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0', fontWeight: 700, color: '#0F172A' }}>
          Transactions en attente (72 dernières heures) — {items.length}
        </div>
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }} data-testid="reconcile-empty">
            Aucune transaction en attente.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>Créé le</th>
                  <th style={{ padding: 10 }}>Patient</th>
                  <th style={{ padding: 10 }}>Téléphone</th>
                  <th style={{ padding: 10 }}>Montant</th>
                  <th style={{ padding: 10 }}>SerdiPay ID</th>
                  <th style={{ padding: 10 }}>Statut</th>
                  <th style={{ padding: 10, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.transaction_id} style={{ borderTop: '1px solid #E2E8F0' }} data-testid={`reconcile-row-${it.transaction_id}`}>
                    <td style={{ padding: 10 }}>{new Date(it.created_at).toLocaleString('fr-FR')}</td>
                    <td style={{ padding: 10 }}>{it.patient_name || '—'}</td>
                    <td style={{ padding: 10, fontFamily: 'monospace' }}>{it.phone_number || '—'}</td>
                    <td style={{ padding: 10 }}>
                      <strong>{it.amount}</strong> {it.currency}
                    </td>
                    <td style={{ padding: 10, fontFamily: 'monospace', color: '#1D4ED8' }}>
                      {it.serdipay_transaction_id || '—'}
                    </td>
                    <td style={{ padding: 10 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', fontSize: '0.75rem', fontWeight: 600 }}>
                        {it.status}
                      </span>
                    </td>
                    <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => reconcile({ transaction_id: it.transaction_id, status: 'completed', note: 'Admin reconcile' })}
                        disabled={busyId === it.transaction_id}
                        className="btn-success"
                        data-testid={`reconcile-approve-${it.transaction_id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 6, padding: '6px 10px', background: '#16A34A', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer' }}
                      >
                        <CheckCircle2 size={14} /> Confirmer
                      </button>
                      <button
                        onClick={() => reconcile({ transaction_id: it.transaction_id, status: 'failed', note: 'Admin reject' })}
                        disabled={busyId === it.transaction_id}
                        data-testid={`reconcile-reject-${it.transaction_id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#DC2626', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer' }}
                      >
                        <XCircle size={14} /> Rejeter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Webhook debug log */}
      <div style={{ marginTop: 24, background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
        <button
          onClick={() => setShowWebhooks((s) => !s)}
          data-testid="reconcile-toggle-webhooks"
          style={{ width: '100%', padding: '12px 16px', textAlign: 'left', background: '#F1F5F9', border: 0, cursor: 'pointer', fontWeight: 700, color: '#0F172A' }}
        >
          {showWebhooks ? '▼' : '▶'} Webhooks reçus récemment ({webhooks.length}) — debug
        </button>
        {showWebhooks && (
          <div style={{ padding: 16, maxHeight: 400, overflow: 'auto', fontSize: '0.8rem', fontFamily: 'monospace' }}>
            {webhooks.length === 0 ? (
              <div style={{ color: '#64748b' }}>Aucun webhook reçu de SerdiPay. C'est probablement pour ça que les paiements restent en attente.</div>
            ) : (
              webhooks.map((w, i) => (
                <div key={i} style={{ padding: 8, borderBottom: '1px solid #E2E8F0' }}>
                  <div style={{ color: '#0F172A', fontWeight: 700 }}>{w.received_at} — de {w.client_ip}</div>
                  <div style={{ color: '#475569', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: 4 }}>{w.raw_body}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPaymentsReconcile;
